import React, { useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Training as TrainingModule } from "./Training";
import { TrainingListControl, ComplianceItem } from "@/components/ui/TrainingListControl";

const GET_USER_COMPLIANCE_STATUS = gql`
  query GetUserComplianceStatus {
    getUserComplianceStatus {
      id
      name
      item
      pdf
      frequencyDays
      lastCompletionDate
      nextDueDate
      dueInDays
      status
      required
      source
    }
  }
`;

type Props = {
  mode: "mandatory" | "optional";
  onCleared?: () => void;
  overdueOnly?: boolean;
};

export const ComplianceTrainingList: React.FC<Props> = ({ mode, onCleared, overdueOnly = false }) => {
  const { data, loading, refetch } = useQuery(GET_USER_COMPLIANCE_STATUS, {
    fetchPolicy: "network-only",
  });
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState<string | null>(null);

  const items: ComplianceItem[] = useMemo(() => data?.getUserComplianceStatus || [], [data]);

  const blockingItems = useMemo(() => {
    return items.filter((item) => {
      if (!item.required) {
        return false;
      }

      if (typeof item.dueInDays !== "number") {
        return true;
      }

      return item.dueInDays <= 7;
    });
  }, [items]);

  const mandatoryItems = useMemo(() => {
    const isOverdue = (item: ComplianceItem) => {
      if (item.status === "PAST_DUE") {
        return true;
      }

      if (item.dueInDays === null || item.dueInDays === undefined) {
        return true;
      }

      return false;
    };

    const overdueItems = blockingItems.filter(isOverdue);

    if (overdueOnly && overdueItems.length > 0) {
      return overdueItems;
    }

    if (overdueOnly) {
      return blockingItems;
    }

    if (overdueItems.length > 0) {
      return overdueItems;
    }

    return blockingItems;
  }, [blockingItems, overdueOnly]);

  const visibleItems = useMemo(() => {
    if (mode === "mandatory") {
        return mandatoryItems;
      }

      return items;
    }, [items, mandatoryItems, mode]);

  const allCleared = useMemo(() => {
    if (mode !== "mandatory") {
      return false;
    }

    if (loading) {
      return false;
    }

    return mandatoryItems.length === 0;
  }, [loading, mandatoryItems, mode]);

  useEffect(() => {
    if (!allCleared) {
      return;
    }

    if (!onCleared) {
      return;
    }

    onCleared();
  }, [allCleared, onCleared]);

  const renderHeader = () => {
    if (mode === "mandatory") {
      return null;
    }

    return (
      <div className="mb-4">
        <div className="text-lg font-semibold text-gray-900">Available trainings</div>
        <div className="text-sm text-gray-600">
          Review upcoming compliance items and optional education resources.
        </div>
      </div>
    );
  };

  if (loading) {
    return null;
  }

  if (mode === "mandatory" && activeItem) {
    return (
      <div className="w-full space-y-4">
        {renderHeader()}
        <div className="rounded border border-gray-200 bg-white p-4">
          <TrainingModule
            embedded
            item={activeItem}
            onDone={async () => {
              await refetch();
              setActiveItem(null);
            }}
          />
        </div>
      </div>
    );
  }

  if (mode === "mandatory" && visibleItems.length === 0) {
    return null;
  }

  const openTraining = (item: ComplianceItem) => {
    const route = `/support/training/${encodeURIComponent(item.item)}`;
    if (mode !== "mandatory") {
      navigate(route);
      return;
    }

    const key = item.item || item.name || "training";
    setActiveItem(key);
  };

  const requiredItems = visibleItems.filter((item) => item.required);
  const optionalItems = visibleItems.filter((item) => !item.required);

  return (
    <TrainingListControl
      requiredItems={requiredItems}
      optionalItems={optionalItems}
      onOpen={openTraining}
      header={renderHeader()}
    />
  );
};
